// ===================================
// ENHANCED DASHBOARD WITH PHASE III FEATURES - src/pages/Dashboard.tsx
// ===================================
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { parseUserPermissions } from '../utils/permissions';
import TopicsApi from '../services/topicsApi';
import ActionsApi from '../services/actionsApi';
import { Topic, Action } from '../types';
import { 
  User, 
  Building2, 
  Shield, 
  Clock, 
  Settings, 
  Activity,
  Users,
  FileText,
  Zap,
  CheckCircle,
  AlertTriangle,
  Plus,
  ArrowRight,
  TrendingUp,
  Database,
  BarChart3,
  Workflow,
  Search,
  RefreshCw
} from 'lucide-react';

interface DashboardStats {
  totalTopics: number;
  totalActions: number;
  recentlyUpdatedActions: number;
  validFlows: number;
  invalidFlows: number;
  actionsByTopic: Array<{ topic_name: string; action_count: number }>;
  recentActivity: Array<{
    type: 'topic' | 'action';
    name: string;
    updated_at: string;
    topic?: string;
  }>;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const permissions = parseUserPermissions(user);

  // State for dashboard data
  const [stats, setStats] = useState<DashboardStats>({
    totalTopics: 0,
    totalActions: 0,
    recentlyUpdatedActions: 0,
    validFlows: 0,
    invalidFlows: 0,
    actionsByTopic: [],
    recentActivity: []
  });
  
  const [recentTopics, setRecentTopics] = useState<Topic[]>([]);
  const [popularTopics, setPopularTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Load dashboard data
  const loadDashboardData = async (showRefreshIndicator = false) => {
    if (!permissions.canRead) {
      setLoading(false);
      return;
    }

    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Load topics data
      const [topicsResponse, recentTopicsResponse, popularTopicsResponse] = await Promise.all([
        TopicsApi.getTopics({ 
          business_id: permissions.isGodMode ? undefined : user?.business_id,
          limit: 1000 // Get all for stats
        }),
        TopicsApi.getTopics({
          business_id: permissions.isGodMode ? undefined : user?.business_id,
          limit: 5,
          sort_by: 'updated_at',
          sort_order: 'desc'
        }),
        TopicsApi.getTopics({
          business_id: permissions.isGodMode ? undefined : user?.business_id,
          limit: 5,
          sort_by: 'action_count',
          sort_order: 'desc'
        })
      ]);

      const allTopics = topicsResponse.items;
      setRecentTopics(recentTopicsResponse.items);
      setPopularTopics(popularTopicsResponse.items);

      // Calculate aggregate statistics
      let totalActions = 0;
      let recentlyUpdatedActions = 0;
      const actionsByTopic: Array<{ topic_name: string; action_count: number }> = [];
      const recentActivity: Array<any> = [];

      // Add topics to recent activity
      recentTopicsResponse.items.forEach(topic => {
        recentActivity.push({
          type: 'topic' as const,
          name: topic.name,
          updated_at: topic.updated_at
        });
      });

      // Process each topic for action statistics
      for (const topic of allTopics) {
        totalActions += topic.action_count;
        
        if (topic.action_count > 0) {
          actionsByTopic.push({
            topic_name: topic.name,
            action_count: topic.action_count
          });

          // Get recent actions for this topic
          try {
            const topicActions = await ActionsApi.getTopicActions({
              topic_id: topic.id,
              limit: 5,
              sort_by: 'updated_at',
              sort_order: 'desc'
            });

            // Count recently updated actions (last 7 days)
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

            topicActions.items.forEach(action => {
              const updatedDate = new Date(action.updated_at);
              if (updatedDate > sevenDaysAgo) {
                recentlyUpdatedActions++;
              }

              // Add to recent activity
              recentActivity.push({
                type: 'action' as const,
                name: action.action_name,
                updated_at: action.updated_at,
                topic: topic.name
              });
            });
          } catch (error) {
            console.warn(`Failed to load actions for topic ${topic.name}:`, error);
          }
        }
      }

      // Sort recent activity by date
      recentActivity.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

      // Sort actions by topic
      actionsByTopic.sort((a, b) => b.action_count - a.action_count);

      setStats({
        totalTopics: allTopics.length,
        totalActions,
        recentlyUpdatedActions,
        validFlows: 0, // Would need to implement flow validation batch check
        invalidFlows: 0,
        actionsByTopic: actionsByTopic.slice(0, 10), // Top 10
        recentActivity: recentActivity.slice(0, 10) // Most recent 10
      });

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [permissions.canRead, user?.business_id, permissions.isGodMode]);

  // Handle refresh
  const handleRefresh = () => {
    loadDashboardData(true);
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Welcome back, {user.name}! Here's your action management overview.
          </p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
          {permissions.canCreate && (
            <button
              onClick={() => navigate('/topics')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Topic
            </button>
          )}
        </div>
      </div>

      {/* User Info Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900">{user.name}</h2>
            <p className="text-gray-600">{user.email}</p>
            <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <Shield className="h-4 w-4" />
                <span>{user.role_name}</span>
              </div>
              <div className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                <span>{user.current_business}</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                <span>Business ID: {user.business_id}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Phase III Success Notice */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <h3 className="text-lg font-medium text-green-900">Phase III: Actions CRUD Complete! 🎉</h3>
        </div>
        <p className="text-green-700 mb-4">
          Full actions management is now operational with schema-driven forms, Salesforce integration, and advanced features
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <h4 className="font-medium text-green-900">✅ New Features Available:</h4>
            <div className="space-y-1 text-green-700">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>Actions CRUD with dynamic forms</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>Salesforce flow validation & picklists</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>Schema-driven input/output fields</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>Role-based permissions (Manager+ delete)</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <h4 className="font-medium text-green-900">🚀 Coming in Phase IV:</h4>
            <div className="space-y-1 text-green-600">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>CSV import/export with new schema</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>Bulk operations & validation</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>Advanced search & analytics</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>Enhanced audit logs viewer</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Topics</p>
              {loading ? (
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-16 mt-1"></div>
                </div>
              ) : (
                <p className="text-3xl font-bold text-gray-900">{stats.totalTopics}</p>
              )}
              <p className="text-xs text-gray-500">
                {permissions.canRead ? 'Total available' : 'No read access'}
              </p>
            </div>
            <FileText className="h-8 w-8 text-blue-500" />
          </div>
          {permissions.canRead && (
            <div className="mt-4">
              <button
                onClick={() => navigate('/topics')}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
              >
                View all topics
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Actions</p>
              {loading ? (
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-16 mt-1"></div>
                </div>
              ) : (
                <p className="text-3xl font-bold text-gray-900">{stats.totalActions}</p>
              )}
              <p className="text-xs text-gray-500">
                Across all topics
              </p>
            </div>
            <Zap className="h-8 w-8 text-green-500" />
          </div>
          {permissions.canRead && (
            <div className="mt-4">
              <button
                onClick={() => navigate('/topics')}
                className="text-green-600 hover:text-green-800 text-sm flex items-center gap-1"
              >
                Manage actions
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Recent Updates</p>
              {loading ? (
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-16 mt-1"></div>
                </div>
              ) : (
                <p className="text-3xl font-bold text-gray-900">{stats.recentlyUpdatedActions}</p>
              )}
              <p className="text-xs text-gray-500">
                Actions updated this week
              </p>
            </div>
            <Activity className="h-8 w-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Salesforce Flows</p>
              {loading ? (
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-16 mt-1"></div>
                </div>
              ) : (
                <p className="text-3xl font-bold text-gray-900">{stats.totalActions}</p>
              )}
              <p className="text-xs text-gray-500">
                Flow integrations
              </p>
            </div>
            <Workflow className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Charts and Lists */}
      {permissions.canRead && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Actions by Topic Chart */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Actions by Topic
              </h3>
              <button
                onClick={() => navigate('/topics')}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                View all
              </button>
            </div>
            
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-4 bg-gray-200 rounded"></div>
                      <div className="flex-1 h-4 bg-gray-200 rounded"></div>
                      <div className="w-8 h-4 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : stats.actionsByTopic.length > 0 ? (
              <div className="space-y-3">
                {stats.actionsByTopic.map((item, index) => (
                  <div key={item.topic_name} className="flex items-center gap-3">
                    <div className="w-6 text-center">
                      <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {item.topic_name}
                        </span>
                        <span className="text-sm text-gray-600">{item.action_count}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ 
                            width: `${Math.max(10, (item.action_count / Math.max(...stats.actionsByTopic.map(t => t.action_count))) * 100)}%`
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BarChart3 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">No data available</p>
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Recent Activity
              </h3>
              <button
                onClick={handleRefresh}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Refresh
              </button>
            </div>
            
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : stats.recentActivity.length > 0 ? (
              <div className="space-y-3">
                {stats.recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      activity.type === 'topic' 
                        ? 'bg-blue-100 text-blue-600' 
                        : 'bg-green-100 text-green-600'
                    }`}>
                      {activity.type === 'topic' ? (
                        <FileText className="h-4 w-4" />
                      ) : (
                        <Zap className="h-4 w-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {activity.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {activity.type === 'action' && activity.topic && (
                          <span>in {activity.topic} • </span>
                        )}
                        {new Date(activity.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Activity className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">No recent activity</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Popular Topics (existing functionality enhanced) */}
      {permissions.canRead && popularTopics.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Most Active Topics
            </h3>
            <button
              onClick={() => navigate('/topics')}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              View all
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {popularTopics.map((topic, index) => (
              <div
                key={topic.id}
                onClick={() => navigate('/topics')}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-sm font-bold text-green-600">#{index + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{topic.name}</p>
                    <p className="text-xs text-gray-500">
                      {topic.action_count} action{topic.action_count !== 1 ? 's' : ''} • Updated {new Date(topic.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {permissions.canCreate && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Quick Actions
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/topics')}
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Plus className="h-5 w-5 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">Create Topic</p>
                <p className="text-xs text-gray-500">Start organizing your actions</p>
              </div>
            </button>
            
            <button
              onClick={() => navigate('/topics')}
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors"
            >
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Zap className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">Add Actions</p>
                <p className="text-xs text-gray-500">Build Salesforce integrations</p>
              </div>
            </button>
            
            <button
              onClick={() => navigate('/health')}
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
            >
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Database className="h-5 w-5 text-purple-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">Check Health</p>
                <p className="text-xs text-gray-500">System status & connectivity</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;